import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatusBadge from "@/components/common/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChatTeardropText } from "@phosphor-icons/react";

const FILTERS = [
  ["", "All"],
  ["near_10", "Near 10"],
  ["near_20", "Near 20"],
  ["near_30", "Near 30 (Bali)"],
  ["high_potential", "High Potential"],
  ["watchlist", "Watchlist"],
];

export default function CRM() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("active");
  const [nextDate, setNextDate] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.filter = filter;
      const { data } = await api.get("/crm", { params });
      setRows(data);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const submit = async () => {
    if (!target) return;
    try {
      await api.post(`/crm/${target.id}/notes`, {
        note,
        follow_up_status: status,
        next_follow_up_date: nextDate || null,
      });
      toast.success("Note added");
      setTarget(null); setNote(""); setNextDate("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1600px] mx-auto">
      <PageHeader title="CRM Follow-up" subtitle="Track engagement, log conversations, and surface participants close to a milestone." breadcrumbs="Sales / CRM" testId="crm-page" />

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map(([k, label]) => (
          <button key={k || "all"} data-testid={`crm-filter-${k || "all"}`} onClick={() => setFilter(k)} className={`px-3 py-1.5 text-xs rounded-md border ${filter === k ? "bg-blue-500/10 border-blue-500 text-blue-300" : "border-white/10 text-gray-400 hover:text-white"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="bg-[#111827] border border-white/10 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 bg-[#1F2937]/50 text-left text-[10px] uppercase tracking-widest text-gray-500">
                <th className="px-4 py-3">Participant</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3 text-right">Lot</th>
                <th className="px-4 py-3 text-right">Next milestone</th>
                <th className="px-4 py-3 text-right">Remaining</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={7} className="p-8 text-center text-gray-500">Loading…</td></tr> :
                rows.length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-gray-500">No matches</td></tr> :
                rows.map((p) => (
                  <tr key={p.id} data-testid={`crm-row-${p.id}`} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <p className="text-white">{p.full_name}</p>
                      <p className="text-xs font-mono text-gray-500">{p.broker_account_number}</p>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400">
                      <p>{p.phone}</p>
                      <p>{p.telegram_username}</p>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-white">{(p.total_valid_lot || 0).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right font-mono text-orange-300">{p.next_milestone || "—"}</td>
                    <td className="px-4 py-3 text-right font-mono text-gray-300">{p.lot_remaining_to_next ? p.lot_remaining_to_next.toFixed(2) : "—"}</td>
                    <td className="px-4 py-3"><StatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-right">
                      <button data-testid={`crm-note-${p.id}`} onClick={() => setTarget(p)} className="px-3 py-1 text-xs bg-blue-500/10 border border-blue-500/30 text-blue-300 rounded-md inline-flex items-center gap-1 hover:bg-blue-500/20">
                        <ChatTeardropText size={12} /> Note
                      </button>
                    </td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="bg-[#111827] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-display">CRM Note · {target?.full_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <textarea data-testid="crm-note-input" placeholder="Note (e.g., called to remind about next milestone)" rows={4} value={note} onChange={(e) => setNote(e.target.value)} className="w-full bg-[#0B0F19] border border-white/10 rounded-md p-2.5 text-sm outline-none" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-1.5">Follow-up status</p>
                <select data-testid="crm-status" value={status} onChange={(e) => setStatus(e.target.value)} className="w-full bg-[#0B0F19] border border-white/10 rounded-md p-2 text-sm outline-none">
                  {["new", "active", "near_milestone", "inactive", "high_potential", "watchlist", "qualified", "disqualified"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-gray-500 mb-1.5">Next follow-up</p>
                <input data-testid="crm-next-date" type="date" value={nextDate} onChange={(e) => setNextDate(e.target.value)} className="w-full bg-[#0B0F19] border border-white/10 rounded-md p-2 text-sm text-white outline-none" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setTarget(null)} className="px-4 py-2 text-sm text-gray-400">Cancel</button>
            <button data-testid="crm-submit" onClick={submit} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md">Save Note</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
